// src/components/drawer/AutomationConfigs.tsx

import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Pause as PauseIcon,
  PowerSettingsNew as PowerIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputBase,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useAccount } from "../../../context";
import {
  AutomationConfig,
  AutomationStatus,
  CreateAutomationConfigDto,
  TemplatePurpose,
  UpdateAutomationConfigDto,
  WhatsAppTemplate,
} from "../../../interfaces/all";
import { getAllCampaigns } from "../../../services/api";
import { automationApi } from "../../../services/automation";
import { apiService } from "../../../services/axios";
import Header from "../../chat/Header/Header";

const AUTOMATION_STATUSES: AutomationStatus[] = [
  "INTERESSADO",
  "AGENDAMENTO",
  "CAIXA POSTAL / ENVIO DE MENSAGEM",
  "NÃO COMPLETA LIGAÇÃO / ENVIO DE MENSAGEM",
];

const TEMPLATE_PURPOSES: { value: TemplatePurpose; label: string }[] = [
  { value: "convite", label: "Convite" },
  { value: "confirmacao_agendamento", label: "Confirmação de Agendamento" },
  { value: "follow_up", label: "Follow-up" },
];

import {
  ParameterConfig,
  processFinalParameters,
} from "../../../types/template-config";

// Styled components seguindo o padrão dos outros componentes da sidebar
const Container = styled(Box)`
  width: 100%;
  height: 100%;
  background: #fff;
  border-right: 1px solid #e9edef;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 600px) {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }
`;

const ContentContainer = styled(Box)`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 60px);
`;

type ConfigCardProps = {
  config: AutomationConfig;
  campaigns: any[];
  onShowDetails: (config: AutomationConfig) => void;
  onToggleActive: (config: AutomationConfig) => void;
  onEdit: (config: AutomationConfig) => void;
  onDelete: (id: string) => void;
};

// Componente memoizado para os cards de configuração
const ConfigCard = memo(function ConfigCard({
  config,
  campaigns,
  onShowDetails,
  onToggleActive,
  onEdit,
  onDelete,
}: ConfigCardProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "INTERESSADO":
        return "primary";
      case "AGENDAMENTO":
        return "success";
      case "CAIXA POSTAL / ENVIO DE MENSAGEM":
        return "warning";
      case "NÃO COMPLETA LIGAÇÃO / ENVIO DE MENSAGEM":
        return "info";
      default:
        return "default";
    }
  };

  const getPurposeLabel = (purpose: string) => {
    const purposeObj = TEMPLATE_PURPOSES.find((p) => p.value === purpose);
    return purposeObj?.label || purpose;
  };

  const campaignName = config.campaign_id
    ? (() => {
        const campaign = campaigns.find(
          (c) => c.id.toString() === config.campaign_id?.toString(),
        );
        return campaign ? campaign.name : `Campanha ${config.campaign_id}`;
      })()
    : "Configuração Geral";

  return (
    <Paper
      sx={{
        p: 1.5,
        cursor: "pointer",
        transition: "all 0.2s ease",
        border: "1px solid #e9edef",
        borderRadius: "8px",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        "&:hover": {
          backgroundColor: "#f5f6f6",
          borderColor: "#00a884",
          boxShadow: "0 2px 8px rgba(0, 168, 132, 0.1)",
        },
      }}
      onClick={() => onShowDetails(config)}
    >
      {/* Header do card */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              borderRadius: "6px",
              backgroundColor: config.is_active ? "#e8f5e8" : "#ffebee",
              color: config.is_active ? "#2e7d32" : "#d32f2f",
            }}
          >
            {config.is_active ? (
              <PowerIcon fontSize="small" />
            ) : (
              <PauseIcon fontSize="small" />
            )}
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: "#111b21",
                fontSize: "13px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "200px",
              }}
            >
              {campaignName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#667781",
                fontSize: "11px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "200px",
              }}
            >
              {config.template_name}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleActive(config);
            }}
            sx={{ p: 0.5 }}
          >
            {config.is_active ? (
              <PauseIcon fontSize="small" />
            ) : (
              <PowerIcon fontSize="small" />
            )}
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(config);
            }}
            sx={{ p: 0.5 }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(config._id);
            }}
            sx={{ p: 0.5, color: "#d32f2f" }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Informações principais */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* Status do Lead */}
          <Chip
            label={config.lead_status}
            color={getStatusColor(config.lead_status) as any}
            size="small"
            sx={{ fontSize: "10px", height: "20px" }}
          />

          {/* Finalidade */}
          <Typography
            variant="caption"
            sx={{
              color: "#667781",
              fontSize: "10px",
              fontWeight: 500,
            }}
          >
            {getPurposeLabel(config.template_purpose)}
          </Typography>
        </Box>

        {/* Status Ativo/Inativo */}
        <Chip
          label={config.is_active ? "Ativo" : "Inativo"}
          color={config.is_active ? "success" : "error"}
          size="small"
          sx={{ fontSize: "11px", height: "24px" }}
        />
      </Box>

      {/* Footer com informações adicionais */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pt: 1,
          borderTop: "1px solid #f0f2f5",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "#667781",
            fontSize: "10px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "200px",
          }}
        >
          {config.limits.max_per_day}/dia, {config.limits.max_per_hour}/hora
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: "#667781",
            fontSize: "10px",
          }}
        >
          {config.schedule_config.start_time} -{" "}
          {config.schedule_config.end_time}
        </Typography>
      </Box>
    </Paper>
  );
});

export const AutomationConfigs: React.FC = () => {
  const { account } = useAccount();
  const [configs, setConfigs] = useState<AutomationConfig[]>([]);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [campaignsLoaded, setCampaignsLoaded] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState<AutomationConfig | null>(
    null,
  );
  const [dialogLoading, setDialogLoading] = useState(false);

  // Estados para filtros e paginação
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [purposeFilter, setPurposeFilter] = useState<string>("all");
  const [selectedConfig, setSelectedConfig] = useState<AutomationConfig | null>(
    null,
  );
  const [showDetails, setShowDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Estados para modal de confirmação de exclusão
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<AutomationConfig | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState<CreateAutomationConfigDto>({
    company_id: account?.company_id || "",
    campaign_id: null,
    lead_status: "",
    template_id: "",
    template_name: "",
    template_purpose: "convite",
    template_parameters: [],
    is_active: true,
    schedule_config: {
      enabled: true,
      days_of_week: [1, 2, 3, 4, 5],
      start_time: "08:00",
      end_time: "18:00",
      timezone: "America/Sao_Paulo",
    },
    filters: {
      stores: [],
      attendants: [],
      coordinators: [],
    },
    limits: {
      max_per_day: 100,
      max_per_hour: 10,
    },
  });

  // Estados para seleção de campanhas
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);

  // Estados para configuração de parâmetros
  const [parameterConfigs, setParameterConfigs] = useState<ParameterConfig[]>(
    [],
  );

  // useEffect otimizado para evitar re-renderizações desnecessárias
  useEffect(() => {
    if (account?.company_id) {
      loadConfigs();
    }
  }, [account?.company_id]);

  // Carregar templates apenas uma vez
  useEffect(() => {
    if (account?.company_id && !templatesLoaded) {
      loadTemplates();
    }
  }, [account?.company_id, templatesLoaded]);

  // Carregar campanhas apenas uma vez
  useEffect(() => {
    if (!campaignsLoaded) {
      loadCampaigns();
    }
  }, [campaignsLoaded]);

  // Filtrar configurações baseado nos filtros ativos usando useMemo
  const filteredConfigs = useMemo(() => {
    if (!Array.isArray(configs)) {
      return [];
    }

    return configs.filter((config) => {
      // Filtro por texto de busca
      if (searchText.trim()) {
        const searchLower = searchText.toLowerCase();
        const campaignName = config.campaign_id
          ? (() => {
              const campaign = campaigns.find(
                (c) => c.id.toString() === config.campaign_id?.toString(),
              );
              return campaign
                ? campaign.name
                : `Campanha ${config.campaign_id}`;
            })()
          : "Configuração Geral";

        if (
          !campaignName.toLowerCase().includes(searchLower) &&
          !config.template_name.toLowerCase().includes(searchLower) &&
          !config.lead_status.toLowerCase().includes(searchLower)
        ) {
          return false;
        }
      }

      // Filtro por status
      if (statusFilter !== "all" && config.lead_status !== statusFilter) {
        return false;
      }

      // Filtro por finalidade
      if (
        purposeFilter !== "all" &&
        config.template_purpose !== purposeFilter
      ) {
        return false;
      }

      return true;
    });
  }, [configs, searchText, statusFilter, purposeFilter, campaigns]);

  // Calcular total de páginas e ajustar página atual
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredConfigs.length / 10));
  }, [filteredConfigs.length]);

  // Ajustar página atual se necessário
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const loadConfigs = async () => {
    if (!account?.company_id) return;

    try {
      // Só mostrar loading se não há dados ainda
      if (configs.length === 0) {
        setLoading(true);
      }
      const response = await automationApi.getConfigs(account.company_id);

      if (
        response &&
        "success" in response &&
        response.success &&
        "data" in response
      ) {
        setConfigs(response.data);
        setError(null); // Limpar erros anteriores
      } else {
        setError("Erro ao carregar configurações");
      }
    } catch (error) {
      setError("Erro ao carregar configurações");
      console.error("Erro ao carregar configurações:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    if (!account?.company_id) {
      console.log("❌ Company ID não encontrado:", account);
      return;
    }

    try {
      const response = await apiService.get(
        `/messages/templates?companyId=${account.company_id}`,
      );

      if (
        response &&
        response.status === "success" &&
        "data" in response &&
        response.data &&
        typeof response.data === "object" &&
        "data" in response.data
      ) {
        // A resposta tem a estrutura: response.data.data contém o array de templates
        const templatesData = (response.data as { data: WhatsAppTemplate[] })
          .data;
        setTemplates(templatesData || []);
        setTemplatesLoaded(true);
      }
    } catch (error) {
      console.error("❌ Erro ao carregar templates:", error);
    }
  };

  const loadCampaigns = async () => {
    try {
      const response = await getAllCampaigns();

      if (
        response &&
        response.status === "success" &&
        response.data &&
        "campaigns" in response.data
      ) {
        const campaignsData = response.data as { campaigns: any[] };
        setCampaigns(campaignsData.campaigns || []);
        setCampaignsLoaded(true);
      }
    } catch (error) {
      console.error("❌ Erro ao carregar campanhas:", error);
    }
  };

  const handleOpenDialog = async (config?: AutomationConfig) => {
    // Abrir modal imediatamente
    setOpenDialog(true);
    setDialogLoading(!!config); // Mostrar loading apenas quando editando

    if (config) {
      setEditingConfig(config);
      setFormData({
        company_id: config.company_id,
        campaign_id: config.campaign_id,
        lead_status: config.lead_status,
        template_id: config.template_id,
        template_name: config.template_name,
        template_purpose: config.template_purpose as TemplatePurpose,
        template_parameters: config.template_parameters,
        is_active: config.is_active,
        schedule_config: config.schedule_config,
        filters: config.filters,
        limits: config.limits,
      });

      // Carregar campanhas selecionadas se existir
      if (config.campaign_id) {
        setSelectedCampaigns([config.campaign_id.toString()]);
      } else {
        setSelectedCampaigns([]);
      }

      // Definir o template selecionado imediatamente (sem await)
      const template = templates.find((t) => t.id === config.template_id);
      if (template) {
        // Template encontrado - configurações já estão salvas
      }

      // Não precisamos mais carregar parâmetros aqui - será feito pelo TemplateParameterConfigurator
      setDialogLoading(false);
    } else {
      setEditingConfig(null);
      setFormData({
        company_id: account?.company_id || "",
        campaign_id: null,
        lead_status: "",
        template_id: "",
        template_name: "",
        template_purpose: "convite",
        template_parameters: [],
        is_active: true,
        schedule_config: {
          enabled: true,
          days_of_week: [1, 2, 3, 4, 5],
          start_time: "08:00",
          end_time: "18:00",
          timezone: "America/Sao_Paulo",
        },
        filters: {
          stores: [],
          attendants: [],
          coordinators: [],
        },
        limits: {
          max_per_day: 100,
          max_per_hour: 10,
        },
      });

      // Limpar estados para nova configuração
      setParameterConfigs([]);
      setSelectedCampaigns([]);
      setDialogLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingConfig(null);
    setError(null);
    setDialogLoading(false);
  };

  const handleSave = async () => {
    try {
      setError(null);

      // Processar parâmetros configurados
      let finalParameters: string[] = [];
      if (parameterConfigs.length > 0) {
        finalParameters = processFinalParameters(
          parameterConfigs,
          parameterConfigs.length,
        );
      }

      const configData = {
        ...formData,
        template_parameters: finalParameters,
        campaign_id: selectedCampaigns.length > 0 ? selectedCampaigns[0] : null,
      };

      if (editingConfig) {
        const updateData: UpdateAutomationConfigDto = {
          template_id: configData.template_id,
          template_name: configData.template_name,
          template_purpose: configData.template_purpose,
          template_parameters: configData.template_parameters,
          is_active: configData.is_active,
          schedule_config: configData.schedule_config,
          filters: configData.filters,
          limits: configData.limits,
        };

        const response = await automationApi.updateConfig(
          editingConfig._id,
          updateData,
        );
        if ("success" in response && response.success) {
          await loadConfigs();
          handleCloseDialog();
        } else {
          setError("Erro ao atualizar configuração");
        }
      } else {
        const response = await automationApi.createConfig(configData);
        if ("success" in response && response.success) {
          await loadConfigs();
          handleCloseDialog();
        } else {
          setError("Erro ao criar configuração");
        }
      }
    } catch (error) {
      setError("Erro ao salvar configuração");
      console.error("Erro ao salvar configuração:", error);
    }
  };

  const handleDelete = useCallback(
    async (id: string) => {
      const config = configs.find((c) => c._id === id);
      if (config) {
        setConfigToDelete(config);
        setShowDeleteConfirm(true);
      }
    },
    [configs],
  );

  const confirmDelete = useCallback(async () => {
    if (!configToDelete) return;

    try {
      setDeleting(true);
      const response = await automationApi.deleteConfig(configToDelete._id);
      if ("success" in response && response.success) {
        await loadConfigs();
        setShowDeleteConfirm(false);
        setConfigToDelete(null);
      } else {
        setError("Erro ao excluir configuração");
      }
    } catch (error) {
      setError("Erro ao excluir configuração");
      console.error("Erro ao excluir configuração:", error);
    } finally {
      setDeleting(false);
    }
  }, [configToDelete]);

  const cancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setConfigToDelete(null);
    setDeleting(false);
  }, []);

  const handleToggleActive = useCallback(async (config: AutomationConfig) => {
    try {
      const response = config.is_active
        ? await automationApi.deactivateConfig(config._id)
        : await automationApi.activateConfig(config._id);

      if ("success" in response && response.success) {
        await loadConfigs();
      } else {
        setError("Erro ao alterar status da configuração");
      }
    } catch (error) {
      setError("Erro ao alterar status da configuração");
      console.error("Erro ao alterar status:", error);
    }
  }, []);

  const handleTemplateChange = async (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (template) {
      // Template selecionado - configurações já estão salvas na seção Templates
      setFormData((prev) => ({
        ...prev,
        template_id: template.id,
        template_name: template.name,
        template_parameters:
          template.components
            ?.filter((comp: any) => comp.type === "BODY")
            .flatMap((comp: any) => {
              // Extrair parâmetros do texto do template
              const text = comp.text || "";
              const matches = text.match(/\{\{(\d+)\}\}/g);
              return matches || [];
            }) || [],
      }));
    }
  };

  const getPurposeLabel = (purpose: string) => {
    const purposeObj = TEMPLATE_PURPOSES.find((p) => p.value === purpose);
    return purposeObj?.label || purpose;
  };

  // Funções para paginação
  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  // Funções para modal de detalhes
  const handleShowDetails = useCallback((config: AutomationConfig) => {
    setSelectedConfig(config);
    setShowDetails(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setShowDetails(false);
    setSelectedConfig(null);
  }, []);

  const handleEdit = useCallback((config: AutomationConfig) => {
    handleOpenDialog(config);
  }, []);

  if (loading) {
    return (
      <Container>
        <Box sx={{ height: "60px", flexShrink: 0 }}>
          <Header title="Configurações de Automação" />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const startIndex = (currentPage - 1) * 10;
  const endIndex = startIndex + 10;
  const currentConfigs = filteredConfigs.slice(startIndex, endIndex);

  return (
    <Container>
      {/* Header com botão discreto */}
      <Box sx={{ height: "60px", flexShrink: 0, position: "relative" }}>
        <Header title="Configurações de Automação" />
        <Button
          variant="contained"
          size="small"
          startIcon={<SettingsIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            position: "absolute",
            top: "50%",
            right: 16,
            transform: "translateY(-50%)",
            px: 1.5,
            py: 0.5,
            fontSize: "11px",
            borderRadius: "4px",
            backgroundColor: "#00a884",
            textTransform: "none",
            fontWeight: 500,
            minWidth: "auto",
            height: "28px",
            "&:hover": {
              backgroundColor: "#008f6f",
              transform: "translateY(-50%) scale(1.02)",
            },
            transition: "all 0.2s ease-in-out",
            "& .MuiButton-startIcon": {
              marginRight: "4px",
              fontSize: "14px",
            },
          }}
        >
          Nova
        </Button>
      </Box>

      {/* Conteúdo principal */}
      <ContentContainer>
        {/* Erro */}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* Filtros avançados */}
        <Box
          sx={{
            p: 2,
            backgroundColor: "#f0f2f5",
            borderBottom: "1px solid #e9edef",
            flexShrink: 0,
          }}
        >
          <Stack spacing={2}>
            {/* Campo de busca */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#fff",
                borderRadius: "8px",
                padding: "4px 8px",
                border: "1px solid #e9edef",
                "&:focus-within": {
                  borderColor: "#00a884",
                  boxShadow: "0 0 0 2px rgba(0, 168, 132, 0.1)",
                },
              }}
            >
              <SearchIcon sx={{ color: "#54656f", fontSize: 20, mr: 1 }} />
              <InputBase
                placeholder="Buscar por campanha, template ou status..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{
                  flex: 1,
                  fontSize: "14px",
                  color: "#111b21",
                  "&::placeholder": {
                    color: "#667781",
                  },
                }}
              />
            </Box>

            {/* Filtros em linha */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              {/* Filtro por Status */}
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  displayEmpty
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "6px",
                    fontSize: "13px",
                    width: "100%",
                    "& .MuiSelect-select": {
                      padding: "8px 12px",
                      fontSize: "13px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e9edef",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00a884",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00a884",
                    },
                  }}
                >
                  <MenuItem value="all">Todos os Status</MenuItem>
                  {AUTOMATION_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Filtro por Finalidade */}
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select
                  value={purposeFilter}
                  onChange={(e) => setPurposeFilter(e.target.value)}
                  displayEmpty
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "6px",
                    fontSize: "13px",
                    width: "100%",
                    "& .MuiSelect-select": {
                      padding: "8px 12px",
                      fontSize: "13px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e9edef",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00a884",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00a884",
                    },
                  }}
                >
                  <MenuItem value="all">Todas as Finalidades</MenuItem>
                  {TEMPLATE_PURPOSES.map((purpose) => (
                    <MenuItem key={purpose.value} value={purpose.value}>
                      {purpose.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Box>

        {/* Cards de configurações - com scroll apenas nos cards */}
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            p: 2,
            width: "100%",
            minWidth: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          {currentConfigs.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
                flexDirection: "column",
                gap: 2,
                width: "100%",
                minWidth: "100%",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                📭 Nenhuma configuração encontrada
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {configs.length === 0
                  ? "As configurações de automação aparecerão aqui quando forem criadas"
                  : searchText.trim() ||
                      statusFilter !== "all" ||
                      purposeFilter !== "all"
                    ? "Nenhuma configuração encontrada com os filtros aplicados"
                    : "Nenhuma configuração encontrada"}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {currentConfigs.map((config) => (
                <ConfigCard
                  key={config._id}
                  config={config}
                  campaigns={campaigns}
                  onShowDetails={handleShowDetails}
                  onToggleActive={handleToggleActive}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Paginação igual ao ChatSidebar */}
        <Box
          sx={{
            p: 2,
            textAlign: "center",
            borderTop: "1px solid #f0f0f0",
            backgroundColor: "#fff",
            position: "sticky",
            bottom: 0,
            zIndex: 11,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <button
              onClick={goToPreviousPage}
              disabled={loading || currentPage <= 1}
              style={{
                background: currentPage <= 1 || loading ? "#f5f5f5" : "#00a884",
                color: currentPage <= 1 || loading ? "#999" : "white",
                border:
                  currentPage <= 1 || loading ? "1px solid #e0e0e0" : "none",
                borderRadius: "6px",
                padding: "8px 12px",
                cursor: currentPage <= 1 || loading ? "not-allowed" : "pointer",
                fontSize: "12px",
                fontWeight: "500",
                transition: "all 0.2s ease",
                opacity: currentPage <= 1 || loading ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
              onMouseOver={(e) => {
                if (currentPage > 1 && !loading) {
                  e.currentTarget.style.backgroundColor = "#008f6f";
                }
              }}
              onFocus={(e) => {
                if (currentPage > 1 && !loading) {
                  e.currentTarget.style.backgroundColor = "#008f6f";
                }
              }}
              onMouseOut={(e) => {
                if (currentPage > 1 && !loading) {
                  e.currentTarget.style.backgroundColor = "#00a884";
                }
              }}
              onBlur={(e) => {
                if (currentPage > 1 && !loading) {
                  e.currentTarget.style.backgroundColor = "#00a884";
                }
              }}
            >
              {loading && currentPage > 1 ? (
                <>
                  <CircularProgress size={12} sx={{ color: "white" }} />
                  Carregando...
                </>
              ) : (
                "← Anterior"
              )}
            </button>

            <span style={{ fontSize: "12px", color: "#666", padding: "0 8px" }}>
              {currentPage} de {totalPages}
            </span>

            <button
              onClick={goToNextPage}
              disabled={loading || currentPage >= totalPages}
              style={{
                background:
                  currentPage >= totalPages || loading ? "#f5f5f5" : "#00a884",
                color: currentPage >= totalPages || loading ? "#999" : "white",
                border:
                  currentPage >= totalPages || loading
                    ? "1px solid #e0e0e0"
                    : "none",
                borderRadius: "6px",
                padding: "8px 12px",
                cursor:
                  currentPage >= totalPages || loading
                    ? "not-allowed"
                    : "pointer",
                fontSize: "12px",
                fontWeight: "500",
                transition: "all 0.2s ease",
                opacity: currentPage >= totalPages || loading ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
              onMouseOver={(e) => {
                if (currentPage < totalPages && !loading) {
                  e.currentTarget.style.backgroundColor = "#008f6f";
                }
              }}
              onFocus={(e) => {
                if (currentPage < totalPages && !loading) {
                  e.currentTarget.style.backgroundColor = "#008f6f";
                }
              }}
              onMouseOut={(e) => {
                if (currentPage < totalPages && !loading) {
                  e.currentTarget.style.backgroundColor = "#00a884";
                }
              }}
              onBlur={(e) => {
                if (currentPage < totalPages && !loading) {
                  e.currentTarget.style.backgroundColor = "#00a884";
                }
              }}
            >
              {loading && currentPage < totalPages ? (
                <>
                  <CircularProgress size={12} sx={{ color: "white" }} />
                  Carregando...
                </>
              ) : (
                "Próximo →"
              )}
            </button>
          </Box>
        </Box>
      </ContentContainer>

      {/* Modal de Detalhes */}
      <Dialog
        open={showDetails}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
              overflow: "hidden",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            borderBottom: "1px solid #e9edef",
            background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            position: "relative",
          }}
        >
          {/* Elemento decorativo sutil */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 120,
              height: 120,
              background:
                "radial-gradient(circle, rgba(0, 168, 132, 0.05) 0%, transparent 70%)",
              borderRadius: "50%",
              transform: "translate(30px, -30px)",
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              position: "relative",
              zIndex: 1,
            }}
          >
            {selectedConfig && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor: selectedConfig.is_active
                    ? "#e8f5e8"
                    : "#ffebee",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.8)",
                }}
              >
                {selectedConfig.is_active ? (
                  <PowerIcon fontSize="small" />
                ) : (
                  <PauseIcon fontSize="small" />
                )}
              </Box>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#111b21",
                  mb: 0.5,
                  fontSize: "18px",
                }}
              >
                {selectedConfig?.campaign_id
                  ? (() => {
                      const campaign = campaigns.find(
                        (c) =>
                          c.id.toString() ===
                          selectedConfig?.campaign_id?.toString(),
                      );
                      return campaign
                        ? campaign.name
                        : `Campanha ${selectedConfig?.campaign_id}`;
                    })()
                  : "Configuração Geral"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#667781",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {selectedConfig?.template_name}
              </Typography>
            </Box>
            {selectedConfig && (
              <Chip
                label={selectedConfig.is_active ? "Ativo" : "Inativo"}
                color={selectedConfig.is_active ? "success" : "error"}
                size="small"
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  height: "28px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                }}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedConfig && (
            <Box>
              {/* Seção de Informações */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "#111b21",
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  ℹ️ Informações da Configuração
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Status do Lead
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#111b21",
                        mt: 0.5,
                      }}
                    >
                      {selectedConfig.lead_status}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Finalidade
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#111b21",
                        mt: 0.5,
                      }}
                    >
                      {getPurposeLabel(selectedConfig.template_purpose)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Limites Diários
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#111b21",
                        mt: 0.5,
                      }}
                    >
                      {selectedConfig.limits.max_per_day}/dia
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Limites por Hora
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#111b21",
                        mt: 0.5,
                      }}
                    >
                      {selectedConfig.limits.max_per_hour}/hora
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Horário de Funcionamento
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#111b21",
                        mt: 0.5,
                      }}
                    >
                      {selectedConfig.schedule_config.start_time} -{" "}
                      {selectedConfig.schedule_config.end_time}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            borderTop: "1px solid #e9edef",
            backgroundColor: "#fff",
            justifyContent: "center",
          }}
        >
          <Button
            onClick={handleCloseDetails}
            variant="contained"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.2,
              fontSize: "13px",
              backgroundColor: "#00a884",
              boxShadow: "0 2px 8px rgba(0, 168, 132, 0.3)",
              "&:hover": {
                backgroundColor: "#008f6f",
                boxShadow: "0 4px 12px rgba(0, 168, 132, 0.4)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Configuração */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        disableEscapeKeyDown={dialogLoading}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              border: "1px solid rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(10px)",
              maxWidth: "90vw",
              maxHeight: "90vh",
              overflow: "hidden",
              animation: "modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "@keyframes modalSlideIn": {
                "0%": {
                  opacity: 0,
                  transform: "scale(0.9) translateY(-20px)",
                },
                "100%": {
                  opacity: 1,
                  transform: "scale(1) translateY(0)",
                },
              },
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            borderBottom: "1px solid #e9edef",
            background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            position: "relative",
          }}
        >
          {/* Elemento decorativo sutil */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 120,
              height: 120,
              background:
                "radial-gradient(circle, rgba(0, 168, 132, 0.05) 0%, transparent 70%)",
              borderRadius: "50%",
              transform: "translate(30px, -30px)",
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 48,
                height: 48,
                borderRadius: "12px",
                backgroundColor: "#e3f2fd",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.8)",
              }}
            >
              <SettingsIcon sx={{ color: "#1976d2", fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#111b21",
                  mb: 0.5,
                  fontSize: "18px",
                }}
              >
                {editingConfig ? "Editar Configuração" : "Nova Configuração"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#667781",
                  fontSize: "14px",
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                {editingConfig
                  ? "Modificar configuração existente"
                  : "Criar nova configuração de automação"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 4 }}>
          {dialogLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
                flexDirection: "column",
                gap: 2,
              }}
            >
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary">
                Carregando configuração...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Status do Lead</InputLabel>
                  <Select
                    value={formData.lead_status}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        lead_status: e.target.value,
                      }))
                    }
                    label="Status do Lead"
                  >
                    {AUTOMATION_STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Template</InputLabel>
                  <Select
                    value={formData.template_id}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    label="Template"
                  >
                    {templates.length > 0 ? (
                      templates.map((template) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>Nenhum template encontrado</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Finalidade</InputLabel>
                  <Select
                    value={formData.template_purpose}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        template_purpose: e.target.value as TemplatePurpose,
                      }))
                    }
                    label="Finalidade"
                  >
                    {TEMPLATE_PURPOSES.map((purpose) => (
                      <MenuItem key={purpose.value} value={purpose.value}>
                        {purpose.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Campanhas</InputLabel>
                  <Select
                    multiple
                    value={selectedCampaigns}
                    onChange={(e) =>
                      setSelectedCampaigns(e.target.value as string[])
                    }
                    input={<OutlinedInput label="Campanhas" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => {
                          const campaign = campaigns.find(
                            (c) => c.id.toString() === value,
                          );
                          return (
                            <Chip
                              key={value}
                              label={campaign?.name || `Campanha ${value}`}
                              size="small"
                              onDelete={() => {
                                setSelectedCampaigns(
                                  selectedCampaigns.filter(
                                    (id) => id !== value,
                                  ),
                                );
                              }}
                              deleteIcon={<CloseIcon />}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {campaigns.map((campaign) => (
                      <MenuItem
                        key={campaign.id}
                        value={campaign.id.toString()}
                      >
                        <Checkbox
                          checked={
                            selectedCampaigns.indexOf(campaign.id.toString()) >
                            -1
                          }
                        />
                        <ListItemText
                          primary={campaign.name}
                          secondary={`ID: ${campaign.id}`}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Divider sx={{ my: 2 }} />
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
              >
                <SettingsIcon sx={{ color: "#00a884", fontSize: 18 }} />
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#111b21", fontSize: "14px" }}
                >
                  Limites de Envio
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  fullWidth
                  label="Máximo por dia"
                  type="number"
                  value={formData.limits?.max_per_day || 100}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      limits: {
                        ...prev.limits!,
                        max_per_day: parseInt(e.target.value),
                      },
                    }))
                  }
                />

                <TextField
                  fullWidth
                  label="Máximo por hora"
                  type="number"
                  value={formData.limits?.max_per_hour || 10}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      limits: {
                        ...prev.limits!,
                        max_per_hour: parseInt(e.target.value),
                      },
                    }))
                  }
                />
              </Box>

              <Divider sx={{ my: 2 }} />
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
              >
                <ScheduleIcon sx={{ color: "#00a884", fontSize: 18 }} />
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#111b21", fontSize: "14px" }}
                >
                  Horário de Funcionamento
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  fullWidth
                  label="Horário de início"
                  type="time"
                  value={formData.schedule_config?.start_time || "08:00"}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      schedule_config: {
                        ...prev.schedule_config!,
                        start_time: e.target.value,
                      },
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  fullWidth
                  label="Horário de fim"
                  type="time"
                  value={formData.schedule_config?.end_time || "18:00"}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      schedule_config: {
                        ...prev.schedule_config!,
                        end_time: e.target.value,
                      },
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        is_active: e.target.checked,
                      }))
                    }
                  />
                }
                label="Configuração ativa"
              />

              {/* Configuração de Parâmetros do Template */}
              {formData.template_id && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <SettingsIcon sx={{ color: "#00a884", fontSize: 18 }} />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: "#111b21",
                        fontSize: "14px",
                      }}
                    >
                      Parâmetros do Template
                    </Typography>
                  </Box>

                  <Alert
                    severity="success"
                    sx={{
                      mb: 2,
                      borderRadius: "8px",
                      "& .MuiAlert-icon": {
                        fontSize: "20px",
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        lineHeight: 1.4,
                      }}
                    >
                      ✅ Os parâmetros deste template já foram configurados na
                      seção &quot;Templates do WhatsApp&quot; e serão aplicados
                      automaticamente nas mensagens automáticas.
                    </Typography>
                  </Alert>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: "1px solid #e9edef",
            background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Button
            onClick={handleCloseDialog}
            disabled={dialogLoading}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.2,
              fontSize: "13px",
              color: "#667781",
              border: "1px solid #e9edef",
              backgroundColor: "#fff",
              "&:hover": {
                backgroundColor: "#f0f2f5",
                borderColor: "#d1d7db",
              },
              "&:disabled": {
                opacity: 0.6,
                cursor: "not-allowed",
              },
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={dialogLoading}
            startIcon={
              dialogLoading ? (
                <CircularProgress size={16} sx={{ color: "white" }} />
              ) : (
                <SettingsIcon />
              )
            }
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.2,
              fontSize: "13px",
              backgroundColor: "#00a884",
              boxShadow: "0 2px 8px rgba(0, 168, 132, 0.3)",
              "&:hover": {
                backgroundColor: "#008f6f",
                boxShadow: "0 4px 12px rgba(0, 168, 132, 0.4)",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
                boxShadow: "none",
                transform: "none",
              },
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            {dialogLoading
              ? "Salvando..."
              : editingConfig
                ? "Atualizar Configuração"
                : "Criar Configuração"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog
        open={showDeleteConfirm}
        onClose={cancelDelete}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown={deleting}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "16px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              border: "1px solid rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(10px)",
              maxWidth: "400px",
              maxHeight: "90vh",
              overflow: "hidden",
              animation: "modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "@keyframes modalSlideIn": {
                "0%": {
                  opacity: 0,
                  transform: "scale(0.9) translateY(-20px)",
                },
                "100%": {
                  opacity: 1,
                  transform: "scale(1) translateY(0)",
                },
              },
              "@keyframes pulse": {
                "0%": {
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                },
                "50%": {
                  boxShadow: "0 2px 8px rgba(211, 47, 47, 0.3)",
                },
                "100%": {
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                },
              },
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 2.5,
            borderBottom: "1px solid #e9edef",
            background: "linear-gradient(135deg, #ffebee 0%, #ffffff 100%)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Elemento decorativo sutil */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 80,
              height: 80,
              background:
                "radial-gradient(circle, rgba(244, 67, 54, 0.05) 0%, transparent 70%)",
              borderRadius: "50%",
              transform: "translate(20px, -20px)",
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 48,
                height: 48,
                borderRadius: "12px",
                backgroundColor: "#ffebee",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.8)",
                animation: "pulse 2s infinite",
              }}
            >
              <DeleteIcon sx={{ color: "#d32f2f", fontSize: "24px" }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#111b21",
                  mb: 0.5,
                  fontSize: "18px",
                  lineHeight: 1.2,
                }}
              >
                Confirmar Exclusão
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#667781",
                  fontSize: "13px",
                  fontWeight: 500,
                  lineHeight: 1.3,
                }}
              >
                Esta ação não pode ser desfeita
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 2.5,
            overflow: "hidden",
            overflowY: "auto",
            maxHeight: "60vh",
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body1"
              sx={{
                color: "#111b21",
                fontSize: "15px",
                fontWeight: 500,
                mb: 2.5,
                wordBreak: "break-word",
                lineHeight: 1.4,
              }}
            >
              Tem certeza que deseja excluir esta configuração de automação?
            </Typography>

            {configToDelete && (
              <Box
                sx={{
                  p: 2.5,
                  backgroundColor: "#f8f9fa",
                  borderRadius: "12px",
                  border: "1px solid #e9edef",
                  mb: 2,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f0f2f5",
                    borderColor: "#d1d7db",
                  },
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: "#111b21",
                    mb: 1.5,
                    wordBreak: "break-word",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "14px",
                    lineHeight: 1.3,
                  }}
                >
                  {configToDelete.campaign_id
                    ? (() => {
                        const campaign = campaigns.find(
                          (c) =>
                            c.id.toString() ===
                            configToDelete?.campaign_id?.toString(),
                        );
                        return campaign
                          ? campaign.name
                          : `Campanha ${configToDelete?.campaign_id}`;
                      })()
                    : "Configuração Geral"}
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#667781",
                      fontSize: "13px",
                      wordBreak: "break-word",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.3,
                    }}
                  >
                    <strong>Template:</strong> {configToDelete.template_name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#667781",
                      fontSize: "13px",
                      wordBreak: "break-word",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.3,
                    }}
                  >
                    <strong>Status:</strong> {configToDelete.lead_status}
                  </Typography>
                </Box>
              </Box>
            )}

            <Alert
              severity="warning"
              sx={{
                mt: 2,
                borderRadius: "8px",
                "& .MuiAlert-icon": {
                  fontSize: "20px",
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  wordBreak: "break-word",
                  lineHeight: 1.4,
                }}
              >
                ⚠️ Esta ação irá excluir permanentemente a configuração e todos
                os dados associados.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2.5,
            borderTop: "1px solid #e9edef",
            backgroundColor: "#fff",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            minHeight: "auto",
          }}
        >
          <Button
            onClick={cancelDelete}
            disabled={deleting}
            variant="outlined"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              py: 1.2,
              fontSize: "13px",
              color: "#667781",
              border: "1px solid #e9edef",
              backgroundColor: "#fff",
              minWidth: "100px",
              flex: "1 1 auto",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#f0f2f5",
                borderColor: "#d1d7db",
                transform: "translateY(-1px)",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              },
              "&:disabled": {
                opacity: 0.5,
                transform: "none",
                boxShadow: "none",
              },
              "&:focus": {
                outline: "2px solid #00a884",
                outlineOffset: "2px",
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            disabled={deleting}
            sx={{
              backgroundColor: "#d32f2f",
              color: "white",
              fontWeight: 600,
              px: 2.5,
              py: 1.2,
              borderRadius: "8px",
              textTransform: "none",
              fontSize: "13px",
              boxShadow: "0 2px 8px rgba(211, 47, 47, 0.3)",
              minWidth: "120px",
              flex: "1 1 auto",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#b71c1c",
                boxShadow: "0 4px 12px rgba(211, 47, 47, 0.4)",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
                boxShadow: "none",
                transform: "none",
              },
              "&:focus": {
                outline: "2px solid #d32f2f",
                outlineOffset: "2px",
              },
            }}
          >
            {deleting ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Excluindo...
              </Box>
            ) : (
              "Excluir Configuração"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AutomationConfigs;
